import { useRef, useState, useEffect } from 'react';
import { getAuthToken } from './authToken';

// Parametry VAD 1:1 z widget.js (produkcja)
const VAD_SILENCE_MS = 1200;
const VAD_MIN_MS = 800;
const VAD_SPEECH_RMS = 0.025;
const VAD_SILENCE_RMS = 0.015;
const MAX_SEC = 120;

interface DictateButtonProps {
  onText: (text: string) => void;
  disabled?: boolean;
}

export default function DictateButton({ onText, disabled = false }: DictateButtonProps) {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const [secs, setSecs] = useState(0);
  const [err, setErr] = useState('');
  const recRef = useRef<any>(null);
  const streamRef = useRef<any>(null);
  const chunksRef = useRef<any[]>([]);
  const timerRef = useRef<any>(null);
  const secsRef = useRef(0);
  const recordingRef = useRef(false);
  const audioCtxRef = useRef<any>(null);
  const analyserRef = useRef<any>(null);
  const rafRef = useRef<any>(null);
  const speechRef = useRef(false);
  const silenceRef = useRef(0);
  const startRef = useRef(0);

  const supported = typeof navigator !== 'undefined' && !!(navigator.mediaDevices && typeof navigator.mediaDevices.getUserMedia === 'function' && (window as any).MediaRecorder);
  // Konwencja jak App.tsx: <768px = mobile (wąski przycisk)
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const W = isMobile ? 60 : 84;

  useEffect(() => {
    if (!err) return;
    const t = setTimeout(() => setErr(''), 6000);
    return () => clearTimeout(t);
  }, [err]);

  if (!supported) {
    return (
      <button type="button" disabled
        title="Ta przeglądarka nie obsługuje nagrywania audio - zaktualizuj przeglądarkę"
        style={{ alignSelf: 'stretch', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, width: W, padding: '8px 2px', border: '1px solid #e5e7eb', background: '#f3f4f6', color: '#9ca3af', borderRadius: 6, fontSize: 10, fontWeight: 600, cursor: 'not-allowed', boxSizing: 'border-box', textAlign: 'center' }}>
        <span style={{ fontSize: isMobile ? 16 : 19, lineHeight: 1 }}>🎙️</span>
        <span>Dyktuj</span>
      </button>
    );
  }

  const cleanupAudio = () => {
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (audioCtxRef.current) { try { audioCtxRef.current.close(); } catch (e) {} audioCtxRef.current = null; }
    analyserRef.current = null;
    speechRef.current = false;
    silenceRef.current = 0;
  };

  const cleanupAll = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (streamRef.current) { try { streamRef.current.getTracks().forEach((t: any) => t.stop()); } catch (e) {} streamRef.current = null; }
    cleanupAudio();
    recRef.current = null;
    chunksRef.current = [];
    secsRef.current = 0;
  };

  const stopAll = () => {
    if (!recordingRef.current) return;
    recordingRef.current = false;
    try { if (recRef.current && recRef.current.state !== 'inactive') recRef.current.stop(); } catch (e) {}
    cleanupAll();
    setRecording(false);
    setSecs(0);
  };

  const vadLoop = () => {
    if (!recordingRef.current) return;
    rafRef.current = requestAnimationFrame(vadLoop);
    const analyser = analyserRef.current;
    if (!analyser) return;
    const buflen = analyser.fftSize;
    const data = new Uint8Array(buflen);
    analyser.getByteTimeDomainData(data);
    let sum = 0;
    for (let i = 0; i < buflen; i++) { const v = (data[i] - 128) / 128; sum += v * v; }
    const rms = Math.sqrt(sum / buflen);
    const now = Date.now();
    const elapsed = now - startRef.current;
    if (rms > VAD_SPEECH_RMS) { speechRef.current = true; silenceRef.current = 0; }
    else if (speechRef.current && rms < VAD_SILENCE_RMS) {
      if (!silenceRef.current) silenceRef.current = now;
      else if (now - silenceRef.current > VAD_SILENCE_MS && elapsed > VAD_MIN_MS) { stopAll(); return; }
    } else { silenceRef.current = 0; }
  };

  const start = async () => {
    if (disabled || busy || recording) return;
    setErr('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const MR: any = (window as any).MediaRecorder;
      const mimes = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];
      const mime = mimes.find((m: string) => MR.isTypeSupported && MR.isTypeSupported(m)) || '';
      let rec: any;
      try { rec = new MR(stream, mime ? { mimeType: mime } : undefined); }
      catch (e) { rec = new MR(stream); }
      recRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (ev: any) => { if (ev && ev.data && ev.data.size) chunksRef.current.push(ev.data); };
      rec.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || mime || 'audio/webm' });
        cleanupAll();
        setRecording(false);
        setSecs(0);
        if (!blob.size) return;
        setBusy(true);
        try {
          const fd = new FormData();
          fd.append('file', blob, 'speech.webm');
          fd.append('language', 'pl');
          const token = getAuthToken();
          const res = await fetch('/api/voice/transcribe', {
            method: 'POST',
            headers: token ? { Authorization: 'Bearer ' + token } : undefined,
            body: fd,
          });
          if (res.ok) {
            const data = await res.json();
            const text = (((data || {}) as any).text || '').trim();
            if (text) onText(text); else setErr('Nie rozpoznano mowy');
          } else {
            let d = '';
            try { d = ((await res.json()) as any).detail || ''; } catch (e) {}
            setErr(res.status === 429 ? 'Limit dyktowania (10/min) — odczekaj chwilę' : 'Błąd ' + res.status + (d ? (': ' + d) : ''));
          }
        } catch (e) {
          setErr('Błąd sieci — spróbuj ponownie');
        }
        setBusy(false);
      };
      recordingRef.current = true;
      speechRef.current = false;
      silenceRef.current = 0;
      startRef.current = Date.now();
      rec.start();
      setRecording(true);
      setSecs(0);
      try {
        const Ctx: any = (window as any).AudioContext || (window as any).webkitAudioContext;
        const ctx = new Ctx();
        audioCtxRef.current = ctx;
        const src = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 1024;
        src.connect(analyser);
        analyserRef.current = analyser;
        if (ctx.state === 'suspended') { ctx.resume().catch(() => {}); }
        vadLoop();
      } catch (e) { /* bez VAD działa ręczne klik-stop */ }
      timerRef.current = setInterval(() => {
        secsRef.current += 1;
        setSecs(secsRef.current);
        if (secsRef.current >= MAX_SEC) stopAll();
      }, 1000);
    } catch (e) {
      cleanupAll();
      setRecording(false);
      setErr('Mikrofon niedostępny');
    }
  };

  const icon = err ? '⚠️' : recording ? '⏺' : busy ? '⏳' : '🎙️';
  const label = err ? err : recording ? ('Mów… ' + secs + ' s') : busy ? 'Czekaj…' : 'Dyktuj';

  const btnStyle: any = {
    alignSelf: 'stretch',
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    gap: 2, width: W, padding: isMobile ? '8px 2px' : '8px 4px', borderRadius: 6,
    fontSize: err ? 10 : (isMobile ? 10 : 11), fontWeight: 600, textAlign: 'center', lineHeight: 1.3,
    boxSizing: 'border-box', cursor: 'pointer',
    border: recording ? '1px solid #fecaca' : '1px solid #d1d5db',
    background: recording ? '#fef2f2' : '#f9fafb',
    color: (err || recording) ? '#dc2626' : '#374151',
    animation: recording ? 'dictatePulse 1s infinite' : undefined,
  };

  return (
    <>
      <style>{'@keyframes dictatePulse{0%,100%{opacity:1}50%{opacity:.4}}'}</style>
      <button type="button" style={btnStyle} disabled={!!disabled || busy}
        onClick={() => { if (recording) stopAll(); else start(); }}
        title={recording ? 'Zakończy się samo po ciszy — klik aby zakończyć teraz' : 'Dyktuj: mów po polsku — po ciszy tekst wstawi się do pola'}>
        <span style={{ fontSize: isMobile ? 16 : 19, lineHeight: 1 }}>{icon}</span>
        <span>{label}</span>
      </button>
    </>
  );
}